import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import apiService from 'services/api';
import quizService from 'services/quiz';
import { filterQuizzes } from 'utils/quizFilters';
import { filterImportableQuizzes } from 'utils/quizImport';

export const PageContext = createContext();

const PageProvider = (props) => {
    const { quizzes, setQuizzes } = useApp();
    const { auth } = useAuth();
    const { setDialog, closeDialog } = useDialog();
    const { message } = useSnackbar();
    const history = useHistory();
    const { t } = useTranslation('quiz');
    const canManageQuizzes = auth?.role === 'instructor';
    const [filters, setFilters] = useState({ open: false, sort: 'none', search: '' });
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [lmsQuizzes, setLmsQuizzes] = useState([]);
    const [selectedQuiz, setSelectedQuiz] = useState(null);
    const [loadingQuizzes, setLoadingQuizzes] = useState(false);
    const [importingQuiz, setImportingQuiz] = useState(false);
    const [quizListFailed, setQuizListFailed] = useState(false);
    const importRequestId = useRef(0);
    const importBusy = useRef(false);
    const visibleQuizzes = useMemo(() => filterQuizzes(quizzes, filters), [quizzes, filters]);

    const resetFilters = () => setFilters((current) => ({ ...current, sort: 'none' }));

    const closeImportDialog = useCallback(() => {
        importRequestId.current += 1;
        importBusy.current = false;
        setImportDialogOpen(false);
        setLoadingQuizzes(false);
        setImportingQuiz(false);
        setSelectedQuiz(null);
        setQuizListFailed(false);
    }, []);

    useEffect(() => {
        closeImportDialog();
        setLmsQuizzes([]);
        return () => {
            importRequestId.current += 1;
            importBusy.current = false;
        };
    }, [auth?.course_id, auth?.role, closeImportDialog]);

    const onClickImport = async () => {
        if (!canManageQuizzes || importBusy.current) return;
        if (!auth?.course_id) {
            message.error(t('import.course-required', 'Choose a course before importing a quiz.'));
            return;
        }
        const requestId = ++importRequestId.current;
        importBusy.current = true;
        setImportDialogOpen(true);
        setLoadingQuizzes(true);
        setLmsQuizzes([]);
        setSelectedQuiz(null);
        setQuizListFailed(false);
        try {
            const fetchedQuizzes = await apiService.getQuizzes({
                access_token: localStorage.getItem('access_token'),
                course_id: auth.course_id,
            });
            if (requestId !== importRequestId.current) return;
            if (
                !Array.isArray(fetchedQuizzes) ||
                fetchedQuizzes.some((quiz) => !quiz || quiz.id == null || typeof quiz.title !== 'string')
            ) {
                throw new Error('Invalid LMS quiz list');
            }
            setLmsQuizzes(filterImportableQuizzes(fetchedQuizzes, quizzes));
        } catch {
            if (requestId === importRequestId.current) {
                setQuizListFailed(true);
                message.error(t('import.list-error', 'Quizzes could not be loaded. Please try again.'));
            }
        } finally {
            if (requestId === importRequestId.current) {
                importBusy.current = false;
                setLoadingQuizzes(false);
            }
        }
    };

    const onImportQuiz = async () => {
        if (!canManageQuizzes || !selectedQuiz || importBusy.current || !auth?.course_id) return;
        const requestId = ++importRequestId.current;
        importBusy.current = true;
        setImportingQuiz(true);
        try {
            const quiz = await apiService.getQuiz({
                access_token: localStorage.getItem('access_token'),
                course_id: auth.course_id,
                quiz_id: selectedQuiz.id,
            });
            if (requestId !== importRequestId.current) return;
            if (!quiz || !Array.isArray(quiz.questions)) throw new Error('Invalid LMS quiz details');
            closeImportDialog();
            history.push({ pathname: '/quizzes/new', state: { quiz: { ...selectedQuiz, ...quiz } } });
        } catch {
            if (requestId === importRequestId.current) {
                message.error(t('import.details-error', 'The quiz could not be imported. Please try again.'));
            }
        } finally {
            if (requestId === importRequestId.current) {
                importBusy.current = false;
                setImportingQuiz(false);
            }
        }
    };

    const onClick = ({ id }) => {
        history.push(`/quizzes/${id}`);
    };

    const onClickEdit = ({ id }) => {
        if (!canManageQuizzes) return;
        // TODO: Implement the quiz editor route in a later part of this feature.
        history.push(`/quizzes/${id}/edit`);
    };

    const onClickDelete = (quiz) => {
        if (!canManageQuizzes) return;
        setDialog({
            open: true,
            message: t('dialog.delete.title', 'Delete Quiz?'),
            description: t('dialog.delete.description', 'This action cannot be undone.'),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.delete', 'Delete'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: async () => {
                try {
                    await quizService.deleteQuiz(quiz.id);
                    setQuizzes((current) => (current || []).filter(({ id }) => id !== quiz.id));
                    closeDialog();
                } catch {
                    message.error(t('message.delete-error', 'The quiz could not be deleted. Please try again.'));
                }
            },
            onCancel: closeDialog,
            onClose: closeDialog,
        });
    };

    return (
        <PageContext.Provider
            {...props}
            value={{
                quizzes: visibleQuizzes,
                hasQuizzes: Boolean(quizzes?.length),
                canManageQuizzes,
                filters,
                setFilters,
                filterCount: filters.sort === 'none' ? 0 : 1,
                resetFilters,
                onClickImport,
                importDialogOpen,
                lmsQuizzes,
                selectedQuiz,
                setSelectedQuiz,
                loadingQuizzes,
                importingQuiz,
                quizListFailed,
                closeImportDialog,
                onImportQuiz,
                onClick,
                onClickEdit,
                onClickDelete,
            }}
        />
    );
};

export default PageProvider;
